import {
  INVENTORY_CAPACITY,
  INVENTORY_COLS,
  ITEM_NAMES,
  ITEM_DESCRIPTIONS,
  GHOST_SCROLL_ID,
  BLUNT_ID,
  HOG_CHARM_ID,
  isFood,
  isFurnitureItem,
  MAX_CARRY_KG,
  carryWeightKg,
  ITEM_WEIGHT_KG,
  itemEquip,
  isWeapon,
  isShield,
  isPetItem,
  gearStat,
  petLevel,
  type InventorySlot,
  type EquipSlot,
  type HandSlot,
  type CosmeticSlot,
  isFishItem,
  FISHING_HAT_ID,
  FISH_HAT_ZONE_BONUS,
  FISH_HAT_SPEED_MULT,
  FISH_RARITY,
  FISH_RARITY_COLORS,
  FISHING_ROD_ID,
  BAIT_ITEM_ID,
  BMX_ID,
  ROD_BAIT_MAX,
  WEED_SEED_ID,
  WATERING_POT_ID,
  POT_WATER_MAX_ML,
  ROLLING_PAPER_ID,
  WEED_BUD_ID,
  WEED_BUDS_PER_BLUNT,
} from "@ghostland/shared";
import { inventoryStore } from "../items/inventoryStore.js";
import { appearanceStore } from "../items/appearanceStore.js";
import { cosmeticStore } from "../items/cosmeticStore.js";
import { weaponStore } from "../items/weaponStore.js";
import { neckStore } from "../items/neckStore.js";
import { workingStore } from "../items/workingStore.js";
import { statsStore } from "../items/statsStore.js";
import { balanceStore } from "../items/balanceStore.js";
import { injectBillStyle, cashIcon, bankIcon } from "./pixelBills.js";
import { itemThumb } from "../items/itemThumb.js";
import { drawItemIcon } from "../items/itemArt.js";
import { Paperdoll } from "./paperdoll.js";
import { injectTheme } from "./theme.js";

/**
 * Inventory screen: LEFT = a live front-facing idle preview of the player's own
 * character + equipment slots (hat / top / bottom / shoes); RIGHT = the 5x4 bag.
 * Right-click a bag item for Equip / Drop. Click an equipped slot to unequip.
 * All mutations go through the server via the actions wired by world3d.
 */

export interface InventoryActions {
  drop: (slotIndex: number) => void;
  /** Load/unload a bagged rod's bait ("fill" needs the worm stack's slot). */
  rodBait: (rod: number, action: "fill" | "empty", worm?: number) => void;
  equip: (slotIndex: number) => void;
  unequip: (slot: EquipSlot | HandSlot | "neck" | CosmeticSlot) => void;
  use: (slot: InventorySlot) => void;
  /** Use the worn neck charm (summon/dismiss the pet). */
  useNeck?: () => void;
  /** Refill a bagged watering pot (server checks the player stands at the water's edge). */
  fillPot?: (slotIndex: number) => void;
  /** Whether the player currently stands close enough to water to fill a pot (grays out Fill). */
  canFillPot?: () => boolean;
  /** Start the 5s blunt-rolling channel from a Rolling Paper's slot (needs buds in the bag). */
  roll?: (slotIndex: number) => void;
  move: (from: number, to: number) => void;
  /** Whether the player's BMX is out in the world (drives the Deploy/Stow label). */
  hasBmxOut?: () => boolean;
  /** Whether the player currently has a pet hog summoned (drives the charm's Summon/Dismiss label). */
  hasPet?: () => boolean;
  /** Toggle whether the player's cosmetics are shown (off ⇒ everyone sees the real gear). */
  setShowCosmetics?: (show: boolean) => void;
}
let actions: InventoryActions | null = null;
export function setInventoryActions(a: InventoryActions): void {
  actions = a;
}

const STYLE_ID = "ghostland-inv-style";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#gl-inv-overlay {
  position: fixed; inset: 0; z-index: 30;
  display: none; align-items: center; justify-content: center;
  background: rgba(4,0,6,.62);
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; color: var(--gl-text);
}
#gl-inv-overlay.open { display: flex; }
#gl-inv-card {
  padding: 20px 22px 22px; background: var(--gl-panel);
  border: 1px solid var(--gl-border); border-radius: 12px;
  box-shadow: 0 16px 50px rgba(0,0,0,.7), 0 0 0 1px rgba(124,58,237,.15);
}
#gl-inv-head { display: flex; align-items: baseline; justify-content: space-between; margin: 0 0 16px; }
#gl-inv-head h1 { margin: 0; font-size: 16px; letter-spacing: .16em; font-family: 'Silkscreen', ui-monospace, monospace; }
#gl-inv-head .hint { font-size: 11px; color: var(--gl-muted); }
#gl-inv-body { display: flex; gap: 22px; align-items: flex-start; }
#gl-inv-stats {
  display: flex; flex-wrap: wrap; gap: 8px 16px; margin-top: 16px; padding-top: 14px;
  border-top: 1px solid var(--gl-border); font-size: 12px; color: var(--gl-text);
}
#gl-inv-stats .gl-stat { color: var(--gl-muted); }
#gl-inv-stats .gl-stat b { color: var(--gl-text); font-weight: 700; }
#gl-inv-stats .gl-stat.over b { color: var(--gl-error); }

/* Equipment paperdoll styles live in paperdoll.ts (shared with the inspect window). */
#gl-inv-left { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.gl-inv-costoggle { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; color: var(--gl-muted); cursor: pointer; user-select: none; }
.gl-inv-costoggle input { cursor: pointer; }

#gl-inv-grid { display: grid; grid-template-columns: repeat(${INVENTORY_COLS}, 1fr); gap: 8px; }
.gl-inv-slot {
  position: relative; width: 48px; height: 48px;
  display: flex; align-items: center; justify-content: center;
  background: var(--gl-panel-2); border: 2px solid var(--gl-border); border-radius: 8px;
}
.gl-inv-slot.filled { border-color: var(--gl-border-focus); cursor: grab; }
.gl-inv-slot.dragging { opacity: .4; }
.gl-inv-slot.drop-hint { border-color: var(--gl-accent, #b48cff); box-shadow: 0 0 0 2px rgba(180,140,255,.4) inset; }
.gl-inv-slot canvas { image-rendering: pixelated; width: 36px; height: 36px; pointer-events: none; }
.gl-inv-qty {
  position: absolute; right: 2px; bottom: 1px;
  font-size: 10px; line-height: 1; padding: 1px 3px;
  background: rgba(5,0,7,.8); border-radius: 4px; color: var(--gl-text);
}
#gl-inv-menu {
  position: fixed; z-index: 31; display: none; min-width: 96px;
  background: var(--gl-panel); border: 1px solid var(--gl-border); border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0,0,0,.6); overflow: hidden;
}
#gl-inv-menu.open { display: block; }
#gl-inv-menu button {
  display: block; width: 100%; text-align: left; padding: 8px 12px;
  background: none; border: none; color: var(--gl-text); cursor: pointer;
  font-family: inherit; font-size: 12px;
}
#gl-inv-menu button:hover:not(:disabled) { background: var(--gl-panel-2); }
#gl-inv-menu button:disabled { opacity: .4; cursor: default; }
#gl-inv-tip {
  position: fixed; z-index: 32; display: none; max-width: 220px; pointer-events: none;
  padding: 8px 10px; background: var(--gl-panel); border: 1px solid var(--gl-border-focus);
  border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,.6);
}
#gl-inv-tip.open { display: block; }
#gl-inv-tip .gl-tip-name { font-size: 12px; font-weight: 700; color: var(--gl-text); }
#gl-inv-tip .gl-tip-meta { font-size: 10px; color: var(--gl-accent, #b48cff); margin-top: 2px; letter-spacing: .5px; }
#gl-inv-tip .gl-tip-desc { font-size: 11px; color: var(--gl-muted); margin-top: 5px; line-height: 1.35; }
#gl-inv-tip .gl-tip-stats { font-size: 11px; font-weight: 700; margin-top: 5px; }
#gl-inv-tip .gl-tip-baitwrap { display: flex; align-items: center; gap: 8px; margin-top: 6px; }
#gl-inv-tip .gl-tip-baitlabel { font-size: 10px; letter-spacing: 1px; color: #9aa4ae; }
#gl-inv-tip .gl-tip-baitslot {
  position: relative; width: 36px; height: 36px; border: 1px dashed var(--gl-border-focus);
  border-radius: 4px; background: rgba(255,255,255,.04); flex: none;
}
#gl-inv-tip .gl-tip-baitslot.filled { border-style: solid; cursor: pointer; }
#gl-inv-tip .gl-tip-baitslot.filled:hover { background: rgba(255,255,255,.12); }
#gl-inv-tip .gl-tip-baitslot img { width: 100%; height: 100%; image-rendering: pixelated; }
#gl-inv-tip .gl-tip-baitslot .q { position: absolute; right: 2px; bottom: 1px; font: 700 10px ui-monospace,monospace; color: #f4f4f4; text-shadow: 0 1px 2px #000; }
#gl-inv-tip .gl-tip-baitcount { font: 600 11px ui-monospace,monospace; color: var(--gl-text); }
#gl-inv-tip .gl-pet-panel { margin-top: 6px; display: grid; grid-template-columns: 1fr 1fr; gap: 2px 12px; padding: 6px 8px; border: 1px solid rgba(120,220,255,.35); border-radius: 3px; background: rgba(80,200,255,.06); font-family: ui-monospace, monospace; }
#gl-inv-tip .gl-pet-row { display: flex; justify-content: space-between; font-size: 10px; letter-spacing: .5px; }
#gl-inv-tip .gl-pet-row span { color: #7fd0e8; }
#gl-inv-tip .gl-pet-row b { color: var(--gl-text); }
#gl-inv-tip .gl-pet-grow { grid-column: 1 / -1; border-top: 1px solid rgba(120,220,255,.22); margin-top: 2px; padding-top: 3px; }
#gl-inv-tip .gl-pet-grow b { color: #9affc0; }
#gl-inv-tip .gl-tip-base { color: #f2f2f2; }
#gl-inv-tip .gl-tip-roll { color: #74d86a; }
`;

function ensureStyle(): void {
  injectTheme();
  injectBillStyle();
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

let overlay: HTMLDivElement | null = null;
let grid: HTMLDivElement | null = null;
let menu: HTMLDivElement | null = null;
let tip: HTMLDivElement | null = null;
let statsEl: HTMLDivElement | null = null;
let paperdoll: Paperdoll | null = null;
// itemId currently being dragged out of the bag — read by the paperdoll to validate drag-to-equip
// drop targets (dragover can't read the drag payload). Set on dragstart, cleared on dragend.
let draggingItemId: string | null = null;

function ensureOverlay(): HTMLDivElement {
  if (overlay) return overlay;
  ensureStyle();
  overlay = document.createElement("div");
  overlay.id = "gl-inv-overlay";
  overlay.innerHTML = `
    <div id="gl-inv-card">
      <div id="gl-inv-head">
        <h1>Inventory</h1>
        <span class="hint">right-click an item · I or Esc to close</span>
      </div>
      <div id="gl-inv-body">
        <div id="gl-inv-left"></div>
        <div id="gl-inv-right"><div id="gl-inv-grid"></div></div>
      </div>
      <div id="gl-inv-stats"></div>
    </div>
    <div id="gl-inv-menu"></div>
    <div id="gl-inv-tip"></div>
  `;
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeInventory();
    closeMenu();
  });
  document.body.appendChild(overlay);
  grid = overlay.querySelector<HTMLDivElement>("#gl-inv-grid")!;
  menu = overlay.querySelector<HTMLDivElement>("#gl-inv-menu")!;
  tip = overlay.querySelector<HTMLDivElement>("#gl-inv-tip")!;
  tip.addEventListener("mouseenter", () => { if (tipHideTimer !== null) { window.clearTimeout(tipHideTimer); tipHideTimer = null; } });
  tip.addEventListener("mouseleave", hideTip);
  statsEl = overlay.querySelector<HTMLDivElement>("#gl-inv-stats")!;

  // the equipment paperdoll (shared component) — interactive: click a worn item to unequip it.
  paperdoll = new Paperdoll({
    onUnequip: (slot) => actions?.unequip(slot),
    onUseNeck: () => actions?.useNeck?.(),
    onEquip: (slotIndex) => actions?.equip(slotIndex), // drag a bag item onto its gear slot to wear it
    draggingItemId: () => draggingItemId,
  });
  const leftCol = overlay.querySelector<HTMLDivElement>("#gl-inv-left")!;
  leftCol.appendChild(paperdoll.root);
  // "Show cosmetics" toggle — off shows the real gear to everyone (synced).
  const cosToggle = document.createElement("label");
  cosToggle.className = "gl-inv-costoggle";
  cosToggle.innerHTML = `<input type="checkbox" /> Show cosmetics`;
  const cosBox = cosToggle.querySelector("input")!;
  cosBox.addEventListener("change", () => actions?.setShowCosmetics?.(cosBox.checked));
  leftCol.appendChild(cosToggle);
  const SHIFT_LOCK: ReadonlySet<EquipSlot> = new Set(["top", "bottom"]);
  const refreshDoll = (): void => {
    const c = cosmeticStore.get();
    cosBox.checked = c.showCosmetics;
    paperdoll!.setData(appearanceStore.get(), weaponStore.get(), neckStore.get(), workingStore.get() ? SHIFT_LOCK : undefined, statsStore.get().wornMods, { hat: c.hat, top: c.top, bottom: c.bottom, shoes: c.shoes }, c.showCosmetics);
  };

  inventoryStore.subscribe((slots) => { renderGrid(slots); renderStats(); });
  statsStore.subscribe(() => { renderStats(); refreshDoll(); }); // worn mods ride statsStore → refresh the doll's gear tooltips
  balanceStore.subscribe(renderStats);
  appearanceStore.subscribe(refreshDoll);
  cosmeticStore.subscribe(refreshDoll); // re-render the cosmetic slots + figure when cosmetics change
  weaponStore.subscribe(refreshDoll);
  neckStore.subscribe(refreshDoll); // re-render the neck slot when the worn charm changes
  workingStore.subscribe(refreshDoll); // re-lock the shirt/pants slots when a shift starts/ends
  return overlay;
}

/** Carry weight + cash + bank money line at the bottom of the inventory. */
function renderStats(): void {
  if (!statsEl) return;
  const { bankMoney } = statsStore.get();
  const carry = carryWeightKg(inventoryStore.get()); // worms weigh 1kg per stack, not per worm
  const over = carry > MAX_CARRY_KG ? " over" : "";
  statsEl.innerHTML =
    `<span class="gl-stat${over}">WEIGHT <b>${carry} / ${MAX_CARRY_KG}kg</b></span>` +
    `<span class="gl-stat">${cashIcon()} <b>${balanceStore.get().toLocaleString()}</b></span>` +
    `<span class="gl-stat">${bankIcon()} <b>${bankMoney.toLocaleString()}</b></span>`;
}

function renderGrid(slots: InventorySlot[]): void {
  hideTip(); hoveredCell = -1; // cells are rebuilt; a hovered cell vanishes without a mouseleave
  if (!grid) return;
  const bySlot = new Map(slots.map((s) => [s.slotIndex, s]));
  grid.innerHTML = "";
  for (let i = 0; i < INVENTORY_CAPACITY; i++) {
    const cell = document.createElement("div");
    cell.className = "gl-inv-slot";
    cell.dataset.slot = String(i);
    const slot = bySlot.get(i);
    if (slot) {
      cell.classList.add("filled");
      cell.appendChild(itemThumb(slot.itemId));
      if (slot.quantity > 1) {
        const qty = document.createElement("span");
        qty.className = "gl-inv-qty";
        qty.textContent = String(slot.quantity);
        cell.appendChild(qty);
      }
      // fish carry a rarity-colored border so tiers read at a glance in the bag
      if (isFishItem(slot.itemId)) {
        const rc = FISH_RARITY_COLORS[FISH_RARITY[slot.itemId]];
        cell.style.borderColor = rc;
        cell.style.boxShadow = `0 0 0 1px ${rc}40 inset`;
      }
      cell.addEventListener("contextmenu", (e) => {
        e.preventDefault();
        openMenu(e.clientX, e.clientY, slot);
      });
      cell.addEventListener("mouseenter", (e) => { hoveredCell = i; showTip(slot, e.clientX, e.clientY); });
      cell.addEventListener("mousemove", (e) => moveTip(e.clientX, e.clientY));
      // Only schedule a hide if THIS cell is still the hovered one — if the pointer already
      // entered another cell (mouseenter fired first), that cell owns the tooltip now.
      cell.addEventListener("mouseleave", () => { if (hoveredCell === i) { hoveredCell = -1; scheduleHideTip(); } });
      // Drag a filled slot onto another to reorder / merge (server-authoritative move).
      cell.draggable = true;
      cell.addEventListener("dragstart", (e) => {
        hideTip(); closeMenu();
        e.dataTransfer?.setData("text/plain", String(i));
        if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
        cell.classList.add("dragging");
        draggingItemId = slot.itemId; // let the paperdoll validate drag-to-equip targets
      });
      cell.addEventListener("dragend", () => { cell.classList.remove("dragging"); draggingItemId = null; });
    }
    // Every cell (filled or empty) is a drop target.
    cell.addEventListener("dragover", (e) => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = "move"; cell.classList.add("drop-hint"); });
    cell.addEventListener("dragleave", () => cell.classList.remove("drop-hint"));
    cell.addEventListener("drop", (e) => {
      e.preventDefault();
      cell.classList.remove("drop-hint");
      const from = Number(e.dataTransfer?.getData("text/plain"));
      if (!Number.isInteger(from) || from === i) return;
      // Worm Bait dropped onto a rod LOADS it; a rod dropped onto Worm Bait UNLOADS into the bag.
      const src = slots.find((s) => s.slotIndex === from);
      const dst = slots.find((s) => s.slotIndex === i);
      if (src?.itemId === BAIT_ITEM_ID && dst?.itemId === FISHING_ROD_ID) { actions?.rodBait(i, "fill", from); return; }
      if (src?.itemId === FISHING_ROD_ID && dst?.itemId === BAIT_ITEM_ID) { actions?.rodBait(from, "empty"); return; }
      actions?.move(from, i);
    });
    grid.appendChild(cell);
  }
}


function openMenu(x: number, y: number, slot: InventorySlot): void {
  if (!menu) return;
  hideTip();
  menu.innerHTML = "";
  const addBtn = (label: string, fn: () => void, opts?: { disabled?: boolean; title?: string }) => {
    const b = document.createElement("button");
    b.textContent = label;
    if (opts?.disabled) b.disabled = true;
    if (opts?.title) b.title = opts.title;
    b.addEventListener("click", () => { fn(); closeMenu(); });
    menu!.appendChild(b);
  };
  if (slot.itemId === GHOST_SCROLL_ID || slot.itemId === BLUNT_ID || slot.itemId === HOG_CHARM_ID || slot.itemId === BMX_ID) {
    const label = slot.itemId === HOG_CHARM_ID ? (actions?.hasPet?.() ? "Dismiss" : "Summon")
      : slot.itemId === BMX_ID ? (actions?.hasBmxOut?.() ? "Stow" : "Deploy") : "Use";
    addBtn(label, () => actions?.use(slot));
  }
  else if (isFood(slot.itemId)) addBtn(slot.itemId === "water" || slot.itemId === "soda" ? "Drink" : "Eat", () => actions?.use(slot));
  else if (slot.itemId === WEED_SEED_ID) addBtn("Plant", () => actions?.use(slot)); // plants it at your feet (open Kapon ground)
  else if (slot.itemId === WATERING_POT_ID) {
    // Fill needs the player standing at the water's edge; the option shows grayed out otherwise.
    const inRange = actions?.canFillPot?.() ?? false;
    const full = (slot.water ?? 0) >= POT_WATER_MAX_ML;
    addBtn("Fill", () => actions?.fillPot?.(slot.slotIndex), {
      disabled: !inRange || full,
      title: full ? "The pot is already full" : inRange ? "" : "Get close to the water's edge",
    });
  }
  else if (slot.itemId === ROLLING_PAPER_ID) {
    // Roll needs WEED_BUDS_PER_BLUNT buds in the bag; grayed out until you have them.
    const buds = inventoryStore.get().reduce((n, s) => (s.itemId === WEED_BUD_ID ? n + s.quantity : n), 0);
    addBtn("Roll", () => actions?.roll?.(slot.slotIndex), {
      disabled: buds < WEED_BUDS_PER_BLUNT,
      title: buds < WEED_BUDS_PER_BLUNT ? `Needs ${WEED_BUDS_PER_BLUNT}× Weed Bud (you have ${buds})` : "",
    });
  }
  else if (isFurnitureItem(slot.itemId)) addBtn("Place", () => actions?.use(slot)); // enters furniture-placement mode (inside your property)
  if (itemEquip(slot.itemId) || isWeapon(slot.itemId) || isShield(slot.itemId) || isPetItem(slot.itemId) || slot.itemId === FISHING_ROD_ID) addBtn("Equip", () => actions?.equip(slot.slotIndex));
  if (slot.itemId === FISHING_ROD_ID && (slot.bait ?? 0) > 0) addBtn("Empty baits", () => actions?.rodBait(slot.slotIndex, "empty"));
  addBtn("Drop", () => actions?.drop(slot.slotIndex));
  menu.style.left = `${x}px`;
  menu.style.top = `${y}px`;
  menu.classList.add("open");
}

function closeMenu(): void {
  menu?.classList.remove("open");
}

/** Hover tooltip: item name, weight, and flavour/usage text. */
function showTip(slot: InventorySlot, x: number, y: number): void {
  if (!tip) return;
  // Cancel any PENDING hide (armed by mouseleave when the pointer briefly slipped off a cell or
  // crossed between items) — otherwise the stale timer kills this fresh tooltip ~250ms in, and it
  // stays gone while the pointer rests on the item.
  if (tipHideTimer !== null) { window.clearTimeout(tipHideTimer); tipHideTimer = null; }
  const name = ITEM_NAMES[slot.itemId as keyof typeof ITEM_NAMES] ?? slot.itemId;
  const desc = ITEM_DESCRIPTIONS[slot.itemId as keyof typeof ITEM_DESCRIPTIONS] ?? "";
  const meta = `Weight ${ITEM_WEIGHT_KG}kg${slot.quantity > 1 ? ` · x${slot.quantity}` : ""}`;
  // Gear stat block: BASE stats (armor) in white, the rolled RANDOM stat in green after it.
  const g = gearStat(slot.itemId);
  const statName: Record<string, string> = { strength: "Strength", dex: "Dexterity", agility: "Agility" };
  // Rolled per-instance bonus points on armored gear (green), shown after the base stat.
  const m = slot.mods;
  const rolled = m
    ? (["strength", "dex", "agility"] as const).filter((k) => (m[k] ?? 0) > 0).map((k) => `+${m[k]} ${statName[k]}`).join(" ")
    : "";
  const gearLine = g
    ? `<div class="gl-tip-stats"><span class="gl-tip-base">+${g.armor} Armor</span> <span class="gl-tip-roll">+${g.amount} ${statName[g.stat] ?? g.stat}${rolled ? ` ${rolled}` : ""}</span></div>`
    : "";
  // Fishing Hat: green functional-bonus marks (equipment, not cosmetic).
  const fishHatLine = slot.itemId === FISHING_HAT_ID
    ? `<div class="gl-tip-stats"><span class="gl-tip-roll">+${(FISH_HAT_ZONE_BONUS * 10).toFixed(1)} Fishing power</span></div>` +
      `<div class="gl-tip-stats"><span class="gl-tip-roll">\u2212${Math.round((1 - FISH_HAT_SPEED_MULT) * 100)}% Fish movement speed</span></div>`
    : "";
  // Hog Charm: a little hi-tech readout of the bound pet's level + combat stats + grow score.
  const pet = slot.itemId === HOG_CHARM_ID ? slot.pet : undefined;
  const petPanel = pet
    ? `<div class="gl-pet-panel">` +
        `<div class="gl-pet-row"><span>LVL</span><b>${petLevel(pet)}</b></div>` +
        `<div class="gl-pet-row"><span>STR</span><b>${pet.strength.level}</b></div>` +
        `<div class="gl-pet-row"><span>DEX</span><b>${pet.dex.level}</b></div>` +
        `<div class="gl-pet-row"><span>AGI</span><b>${pet.agility.level}</b></div>` +
        `<div class="gl-pet-row gl-pet-grow"><span>GROW</span><b>×${pet.grow.toFixed(2)}</b></div>` +
      `</div>`
    : "";
  // Caught fish: rarity-colored name + this instance's rolled size (unique per fish, persisted).
  const rarity = isFishItem(slot.itemId) ? FISH_RARITY[slot.itemId] : null;
  const rarityColor = rarity ? FISH_RARITY_COLORS[rarity] : "";
  // the rarity-coloured cell border already signals the tier \u2014 no word needed
  const sizeLine = slot.size !== undefined
    ? `<div class="gl-tip-stats"><span class="gl-tip-roll">Size ${slot.size.toFixed(2)}\u2033</span></div>`
    : "";
  // Caught fish provenance: date it was landed (blank for legacy/traded/bought fish).
  const caughtLine = slot.caughtAt
    ? `<div class="gl-tip-meta">Caught ${new Date(slot.caughtAt).toLocaleDateString()}</div>`
    : "";
  // Watering pot: its live tank level (fill it at the water's edge; each watering pours 100ml).
  const waterLine = slot.itemId === WATERING_POT_ID
    ? `<div class="gl-tip-stats"><span class="gl-tip-base" style="color:#86d4f4">Water ${slot.water ?? 0}/${POT_WATER_MAX_ML}ml</span></div>`
    : "";
  // Fishing rod: its BAITS slot — a real slot box holding the loaded worms. Drag Worm Bait onto
  // the rod to load it; CLICK the slot here to return the baits to the bag.
  const bait = slot.bait ?? 0;
  const baitPanel = slot.itemId === FISHING_ROD_ID
    ? `<div class="gl-tip-baitwrap"><span class="gl-tip-baitlabel">BAITS</span>` +
      `<div class="gl-tip-baitslot${bait > 0 ? " filled" : ""}" title="${bait > 0 ? "Click to unload into your bag" : "Empty"}">` +
      (bait > 0 ? `<img src="${wormIconUrl()}" alt=""><span class="q">${bait}</span>` : "") +
      `</div><span class="gl-tip-baitcount">${bait}/${ROD_BAIT_MAX}</span></div>`
    : "";
  tip.innerHTML =
    `<div class="gl-tip-name"${rarity ? ` style="color:${rarityColor}"` : ""}>${name}</div>` +
    `<div class="gl-tip-meta">${meta}</div>` +
    sizeLine +
    caughtLine +
    gearLine +
    fishHatLine +
    petPanel +
    waterLine +
    baitPanel +
    (desc ? `<div class="gl-tip-desc">${desc}</div>` : "");
  tip.classList.add("open");
  // The rod tooltip is INTERACTIVE (its bait slot is clickable) — others stay click-through.
  tip.style.pointerEvents = slot.itemId === FISHING_ROD_ID ? "auto" : "none";
  const baitSlot = tip.querySelector<HTMLDivElement>(".gl-tip-baitslot.filled");
  if (baitSlot) baitSlot.onclick = () => { hideTip(); actions?.rodBait(slot.slotIndex, "empty"); };
  moveTip(x, y);
}

/** Cached data-URL of the worm icon (drawn once from the procedural item art). */
let wormIcon: string | null = null;
function wormIconUrl(): string {
  if (!wormIcon) wormIcon = drawItemIcon(BAIT_ITEM_ID).toDataURL();
  return wormIcon;
}

// Hide with a grace delay so the pointer can travel from the cell INTO the tooltip (the rod's
// bait slot is clickable); entering the tip cancels the hide.
let tipHideTimer: number | null = null;
let hoveredCell = -1; // index of the cell the pointer is over (guards late-firing mouseleave)
function scheduleHideTip(): void {
  if (tipHideTimer !== null) window.clearTimeout(tipHideTimer);
  tipHideTimer = window.setTimeout(() => { tipHideTimer = null; hideTip(); }, 250);
}

function moveTip(x: number, y: number): void {
  if (!tip || !tip.classList.contains("open")) return;
  const pad = 14, w = tip.offsetWidth, h = tip.offsetHeight;
  let left = x + pad, top = y + pad;
  if (left + w > window.innerWidth - 8) left = x - pad - w;
  if (top + h > window.innerHeight - 8) top = y - pad - h;
  tip.style.left = `${Math.max(8, left)}px`;
  tip.style.top = `${Math.max(8, top)}px`;
}

function hideTip(): void {
  if (tipHideTimer !== null) { window.clearTimeout(tipHideTimer); tipHideTimer = null; }
  tip?.classList.remove("open");
}

export function openInventory(): void {
  ensureOverlay().classList.add("open");
  paperdoll?.start();
}

export function closeInventory(): void {
  overlay?.classList.remove("open");
  paperdoll?.stop();
  closeMenu();
}

export function toggleInventory(): void {
  const el = ensureOverlay();
  el.classList.toggle("open");
  if (el.classList.contains("open")) paperdoll?.start();
  else { paperdoll?.stop(); closeMenu(); }
}

export function isInventoryOpen(): boolean {
  return overlay?.classList.contains("open") ?? false;
}
